/**
 * AllowanceTypesManagementPage — list, search, filter allowance types.
 * Standard management page pattern.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeDollarSign,
  ChevronRight,
  CheckCircle,
  Receipt,
  Repeat,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { useDeleteAllowanceType } from '../hooks';
import { localizedName } from '@/utils/localized';
import type { AllowanceType, AllowanceTypeListResponse } from '../types';
import { AllowanceTypeFormModal } from './AllowanceTypeFormModal';
import { AllowanceTypeDetailModal } from './AllowanceTypeDetailModal';
import toast from 'react-hot-toast';
import { useModal } from '@/components/modal';
import {
  PageHeader,
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  SearchInput,
  GlassSelect,
  CreateButton,
  BulkActionBar,
  StatusDot,
  InfiniteScrollSentinel,
} from '@/components/ui';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useRTL } from '@/hooks/useRTL';
import { useSelectionSet } from '@/hooks/useSelectionSet';

const PAGE_SIZE = 20;

export function AllowanceTypesManagementPage() {
  const { t, i18n } = useTranslation();
  const isRTL = useRTL();
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteAllowanceType();
  const { openConfirm } = useModal();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editAllowanceType, setEditAllowanceType] = useState<AllowanceType | null>(null);
  const [selectedAllowanceType, setSelectedAllowanceType] = useState<AllowanceType | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery({
    queryKey: ['allowance-types', 'infinite', search, statusFilter],
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        page: pageParam,
        limit: PAGE_SIZE,
      };
      if (search) params.search = search;
      if (statusFilter) params.isActive = statusFilter;

      const { data } = await http.get<AllowanceTypeListResponse>('/allowance-types', { params });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.allowanceTypes.length, 0);
      return loaded < lastPage.pagination.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage });

  const allAllowanceTypes = useMemo(
    () => data?.pages.flatMap((page) => page.allowanceTypes) || [],
    [data]
  );

  const { selectedIds, isSelected, toggle, selectAll, clear, count, allSelected } = useSelectionSet(allAllowanceTypes);

  const totalCount = data?.pages[0]?.pagination.total || 0;
  const activeCount = useMemo(
    () => allAllowanceTypes.filter((a) => a.isActive).length,
    [allAllowanceTypes]
  );

  const handleBulkDelete = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('allowanceTypes.bulkDeleteTitle'),
      message: t('allowanceTypes.bulkDeleteMessage', { count }),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
        toast.success(t('allowanceTypes.bulkDeletedSuccess'));
        clear();
      },
    });
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* Header */}
      <PageHeader
        icon={<BadgeDollarSign className="w-7 h-7" />}
        title={t('allowanceTypes.management')}
        action={
          <CreateButton
            label={t('allowanceTypes.create')}
            onClick={() => setCreateModalOpen(true)}
          />
        }
      />

      {/* Stats Cards */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<BadgeDollarSign className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('allowanceTypes.total')}
          value={totalCount}
        />
        <StatCard
          icon={<CheckCircle className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('allowanceTypes.active')}
          value={activeCount}
        />
      </motion.div>

      {/* Search & Filters */}
      <motion.div
        variants={staggerItem}
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h2 className="font-semibold text-xs sm:text-sm">
            {t('common.filters')}
          </h2>
        </div>
        <div className="px-6 py-4 flex flex-col md:flex-row gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('allowanceTypes.searchPlaceholder')}
          />

          <button
            onClick={selectAll}
            className="flex items-center gap-2 px-3 py-2 border border-white/30 rounded-lg bg-white/20 cursor-pointer hover:bg-white/20 transition-colors"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={selectAll}
              className="w-5 h-5 pointer-events-none"
              readOnly
            />
            <span className="font-bold text-sm">{t('allowanceTypes.selectAll')}</span>
          </button>

          <GlassSelect
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              triggerHapticFeedback();
            }}
          >
            <option value="">{t('allowanceTypes.allStatuses')}</option>
            <option value="true">{t('allowanceTypes.active')}</option>
            <option value="false">{t('allowanceTypes.inactive')}</option>
          </GlassSelect>
        </div>
      </motion.div>

      {/* List */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('allowanceTypes.loadError')} />
        ) : allAllowanceTypes.length === 0 ? (
          <EmptyState
            icon={<BadgeDollarSign className="w-14 h-14 md:w-16 md:h-16" />}
            message={t('allowanceTypes.noAllowanceTypes')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {allAllowanceTypes.map((allowanceType) => {
              const selected = isSelected(allowanceType.id);

              return (
              <motion.div
                key={allowanceType.id}
                role="button"
                tabIndex={0}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  triggerHapticFeedback();
                  setSelectedAllowanceType(allowanceType);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    triggerHapticFeedback();
                    setSelectedAllowanceType(allowanceType);
                  }
                }}
                className="flex items-center justify-between w-full py-1.5 ps-6 pe-4 md:py-2 md:ps-6 md:pe-4 rounded-xl text-start cursor-pointer glass-surface"
                style={{
                  backgroundColor: ADMIN_COLORS.surface,
                  border: `1px solid ${ADMIN_COLORS.border}`,
                  boxShadow: selected ? ADMIN_COLORS.shadowMd : ADMIN_COLORS.shadow,
                }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Checkbox */}
                  <div
                    className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                    style={{ minWidth: '44px', minHeight: '44px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(allowanceType.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggle(allowanceType.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 pointer-events-none"
                    />
                  </div>

                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{
                      backgroundColor: allowanceType.isActive ? '#ecfdf5' : '#f3f4f6',
                      border: `1px solid ${ADMIN_COLORS.border}`,
                    }}
                  >
                    <BadgeDollarSign className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-xs md:text-sm text-neutral-800 truncate max-w-[200px] md:max-w-none">
                      {localizedName(allowanceType, i18n.language)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span
                        className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                        style={{
                          backgroundColor: ADMIN_COLORS.surface,
                          border: `1px solid ${ADMIN_COLORS.border}`,
                        }}
                      >
                        {allowanceType.code}
                      </span>
                      {allowanceType.isTaxable && (
                        <span
                          className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                          style={{
                            backgroundColor: '#fff3e0',
                            border: `1px solid ${ADMIN_COLORS.border}`,
                          }}
                        >
                          <Receipt className="w-3 h-3 inline-block mr-0.5" />
                          {t('allowanceTypes.taxable')}
                        </span>
                      )}
                      {allowanceType.isRecurring && (
                        <span
                          className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                          style={{
                            backgroundColor: '#e3f2fd',
                            border: `1px solid ${ADMIN_COLORS.border}`,
                          }}
                        >
                          <Repeat className="w-3 h-3 inline-block mr-0.5" />
                          {t('allowanceTypes.recurring')}
                        </span>
                      )}
                      {allowanceType.category && (
                        <span className="text-xs text-slate-500">
                          {allowanceType.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusDot
                    active={allowanceType.isActive}
                    title={allowanceType.isActive ? t('allowanceTypes.active') : t('allowanceTypes.inactive')}
                  />
                  <ChevronRight
                    className="w-5 h-5 text-slate-500"
                    style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                  />
                </div>
              </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Infinite scroll sentinel */}
      <InfiniteScrollSentinel
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sentinelRef={sentinelRef}
      />

      {/* Modals */}
      <AllowanceTypeFormModal
        isOpen={createModalOpen || !!editAllowanceType}
        onClose={() => {
          queryClient.refetchQueries({ queryKey: ['allowance-types'] });
          setCreateModalOpen(false);
          setEditAllowanceType(null);
        }}
        editData={editAllowanceType}
      />

      {selectedAllowanceType && (
        <AllowanceTypeDetailModal
          allowanceType={selectedAllowanceType}
          onClose={() => { queryClient.refetchQueries({ queryKey: ['allowance-types'] }); setSelectedAllowanceType(null); }}
          onEdit={(a) => {
            setSelectedAllowanceType(null);
            setEditAllowanceType(a);
          }}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        count={count}
        onDelete={handleBulkDelete}
        onClear={clear}
        isDeleting={deleteMutation.isPending}
      />
    </motion.div>
  );
}
